import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getGlobalBillingStats, type GlobalBillingStats } from "../api/billing";
import { listProviders } from "../api/providers";
import { useSession } from "../auth/TokenContext";
import { formatCurrencyAmount } from "../components/BillingStatsView";
import { brand } from "../theme";

export function DashboardPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const [stats, setStats] = useState<GlobalBillingStats | null>(null);
  const [providersTotal, setProvidersTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, p] = await Promise.all([
          getGlobalBillingStats(),
          listProviders(),
        ]);
        if (cancelled) return;
        setStats(s);
        setProvidersTotal((p?.providers ?? []).filter((x) => x.available).length);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "No pudimos cargar métricas");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const delta = calcDelta(stats);
  const pygVolume = stats?.volumes?.find((v) => v.currency === "PYG");
  const otherVolumes = stats?.volumes?.filter((v) => v.currency !== "PYG") ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Hola, {session?.user?.name?.split(" ")?.[0] ?? "admin"} 👋
        </Typography>
        <Typography color="text.secondary">
          Resumen del mes — {stats?.period ?? "…"}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricTile
            label="Tx aprobadas"
            value={stats?.approvedCount}
            icon={<CheckCircleIcon sx={{ fontSize: 32, color: "#16A34A" }} />}
            footer={delta && <DeltaChip delta={delta} />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <VolumeTile
            pygVolume={pygVolume?.approvedVolume}
            otherVolumes={otherVolumes}
            loading={!stats}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricTile
            label="Comercios activos"
            value={stats?.activeMerchants}
            icon={<StorefrontIcon sx={{ fontSize: 32, color: brand.navy }} />}
            onClick={() => navigate("/merchants")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricTile
            label="Pasarelas integradas"
            value={providersTotal ?? undefined}
            icon={<BoltIcon sx={{ fontSize: 32, color: brand.navy }} />}
            onClick={() => navigate("/providers")}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Top 5 comercios del mes (PYG)
            </Typography>
            {!stats ? (
              <Stack spacing={1}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height={36} />
                ))}
              </Stack>
            ) : (stats.topMerchants?.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sin transacciones aprobadas este mes todavía.
              </Typography>
            ) : (
              <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
                {(stats.topMerchants ?? []).map((m, idx) => (
                  <Stack
                    key={m.merchantId}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ py: 1.25, cursor: "pointer" }}
                    onClick={() => navigate(`/merchants/${m.merchantId}`)}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        bgcolor: brand.navy,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                      }}
                    >
                      {idx + 1}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                      {m.merchantName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.approvedCount} tx
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                      {formatCurrencyAmount(m.approvedVolumePyg, "PYG")} Gs
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AddBusinessIcon sx={{ color: brand.navy }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Alta rápida
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Creá un comercio nuevo y emití su primera API key.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate("/merchants/new")}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Nuevo comercio
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
}

function MetricTile({
  label,
  value,
  icon,
  onClick,
  footer,
}: {
  label: string;
  value: number | undefined;
  icon: React.ReactNode;
  onClick?: () => void;
  footer?: React.ReactNode;
}) {
  const body = (
    <CardContent>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="overline" color="text.secondary">
            {label}
          </Typography>
          {value === undefined ? (
            <Skeleton width={60} height={42} />
          ) : (
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
          )}
          {footer && <Box sx={{ mt: 0.5 }}>{footer}</Box>}
        </Box>
        {icon}
      </Stack>
    </CardContent>
  );
  return (
    <Card sx={{ height: "100%" }}>
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: "100%" }}>
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
}

function VolumeTile({
  pygVolume,
  otherVolumes,
  loading,
}: {
  pygVolume?: string;
  otherVolumes: { currency: string; approvedVolume: string }[];
  loading: boolean;
}) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            Volumen procesado
          </Typography>
          {loading ? (
            <Skeleton width={140} height={42} />
          ) : (
            <Typography variant="h4" fontWeight={700} sx={{ fontFamily: "monospace" }}>
              {pygVolume ? formatCurrencyAmount(pygVolume, "PYG") : "0"}{" "}
              <Typography component="span" variant="body2" color="text.secondary">
                Gs
              </Typography>
            </Typography>
          )}
          {otherVolumes.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {otherVolumes.map((v) => (
                <Typography key={v.currency} variant="caption" color="text.secondary">
                  · {v.currency} {formatCurrencyAmount(v.approvedVolume, v.currency)}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

interface Delta {
  pct: number;
  direction: "up" | "down" | "flat";
}

function calcDelta(stats: GlobalBillingStats | null): Delta | null {
  if (!stats) return null;
  const { approvedCount, previousApprovedCount } = stats;
  if (previousApprovedCount === 0) {
    return approvedCount > 0
      ? { pct: 100, direction: "up" }
      : { pct: 0, direction: "flat" };
  }
  const pct = ((approvedCount - previousApprovedCount) / previousApprovedCount) * 100;
  if (Math.abs(pct) < 0.5) return { pct: 0, direction: "flat" };
  return { pct: Math.round(pct), direction: pct > 0 ? "up" : "down" };
}

function DeltaChip({ delta }: { delta: Delta }) {
  const iconMap = {
    up: <TrendingUpIcon sx={{ fontSize: 14 }} />,
    down: <TrendingDownIcon sx={{ fontSize: 14 }} />,
    flat: <TrendingFlatIcon sx={{ fontSize: 14 }} />,
  };
  const colorMap = {
    up: "#1b8c4f",
    down: "#b1391a",
    flat: "#64748b",
  };
  return (
    <Chip
      size="small"
      icon={iconMap[delta.direction]}
      label={`${delta.direction === "up" ? "+" : delta.direction === "down" ? "" : "±"}${Math.abs(delta.pct)}% vs mes anterior`}
      sx={{
        bgcolor: "transparent",
        color: colorMap[delta.direction],
        fontWeight: 600,
        "& .MuiChip-icon": { color: colorMap[delta.direction] },
      }}
    />
  );
}
